import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { useTranslation } from 'react-i18next';
import config from '../../config/config.json';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { useCartItems, useRemoveCartItem } from '../../stores/cartItems';
import {
  notifyCartItemAdded,
  notifyCartItemsRemoved,
  notifyOrderPlaced,
} from '../../utils/cartUtils';
import { useQueryClient } from '@tanstack/react-query';
import { secondaryColor } from '../../assets/jss/material-kit-react';
import { useTheme } from '@mui/material/styles';
import hardcoreOff from '../../assets/img/hardcore_off.png';
import ladderOn from '../../assets/img/ladder_on.png';
import ladderOff from '../../assets/img/ladder_off.png';
import hardcoreOn from '../../assets/img/hardcore_on.png';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { getColorFromRarity, getRarityName } from '../../utils/rarityUtils';
import ToolsIcon from 'components/ToolsIcon';
import { useNavigate } from 'react-router-dom';

// Custom styling to prevent layout shift with dropdowns
const styles = {
  container: {
    // Apply this style to any parent container to prevent overall layout shifts
    overflowX: 'hidden',
    // Forces a scrollbar to always be present so layout doesn't shift
    scrollbarGutter: 'stable',
  },
  scrollBox: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    maxHeight: 'calc(100vh-600px)',
    overflowY: 'auto',
    // Ensure scrollbar is always shown to prevent layout shift when dropdown opens
    scrollbarWidth: 'thin',
    scrollbarGutter: 'stable',
    // Add padding to the right to account for scrollbar width
    paddingRight: '6px', // Standard scrollbar width
    boxSizing: 'content-box', // Prevent padding from affecting width
    '&::-webkit-scrollbar': {
      width: '12px',
    },
    '&::-webkit-scrollbar-track': {
      background: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: 'rgba(0,0,0,0.2)',
      borderRadius: '4px',
    },
  },
  selectField: {
    // Ensure select dropdowns don't disrupt layout
    '& .MuiPaper-root': {
      zIndex: 9999,
    },
  },
};

export interface CartItem {
  id: number;
  name: string;
  quantity: number;
  fiat_price: string;
  fg_price: string;
  image_path: string;
  skin_image_path?: string;
  status: string;
  suffix: string;
  metadata?: any;
  stats_description: string;
  ocr_string?: string;
  rarity_id?: string;
  ladder?: number;
  hardcore?: number;
}

export default function CartBubbleContent() {
  const { t } = useTranslation();
  const { data: cartItems = [], isLoading } = useCartItems();
  const removeCartItem = useRemoveCartItem();
  const [totalFiatPrice, setTotalFiatPrice] = useState('0');
  const [totalFgPrice, setTotalFgPrice] = useState('0');
  const sessionToken = localStorage.getItem('session_token');
  const [currency, setCurrency] = useState('fiat');
  const queryClient = useQueryClient();
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const navigate = useNavigate();

  // Track loading state for add operations
  const [addingItemIds, setAddingItemIds] = useState<number[]>([]);
  // Track items that have no more available quantity
  const [unavailableItems, setUnavailableItems] = useState<number[]>([]);

  // Order state
  const [currentGameName, setCurrentGameName] = useState('');
  const [currentGamePassword, setCurrentGamePassword] = useState('');
  const [currentGameRealm, setCurrentGameRealm] = useState('europe');
  const [createTradeHalted, setCreateTradeHalted] = useState(false);
  const [processingOrder, setProcessingOrder] = useState(false);

  // Group items with the same attributes
  const { groupedItems, stockMap } = useMemo(() => {
    const groupMap: Record<string, CartItem[]> = {};
    const stockMap: Record<string, number> = {};

    for (const item of cartItems) {
      const key = [
        item.stats_description || '',
        item.ocr_string || '',
        item.rarity_id || '',
        item.ladder || '',
        item.hardcore || '',
        item.fiat_price || '',
        item.fg_price || '',
      ].join('|');

      if (!groupMap[key]) groupMap[key] = [];
      groupMap[key].push(item);
      stockMap[key] = (stockMap[key] || 0) + 1;
    }

    // Use the first item in each group for rendering
    const groupedItems = Object.values(groupMap).map((items) => items[0]);
    return { groupedItems, stockMap };
  }, [cartItems]);

  // Define getCartTotal first since other functions depend on it
  const getCartTotal = useCallback(async () => {
    if (!sessionToken) {
      return;
    }
    try {
      const response = await axios.get(`${config.backend_base_url}cart/mine`, {
        headers: { 'session-token': sessionToken },
      });
      if (response.data) {
        setTotalFiatPrice(response.data.total_fiat_price || '0');
        setTotalFgPrice(response.data.total_fg_price || '0');
      } else {
        setTotalFiatPrice('0');
        setTotalFgPrice('0');
      }
    } catch (error) {
      console.error('Error fetching cart total:', error);
    }
  }, [sessionToken]);

  // Then define getCartItems which depends on getCartTotal
  const getCartItems = useCallback(async () => {
    if (!sessionToken) {
      return;
    }
    try {
      const url = config.backend_base_url + 'cart/items/all';
      const response = await axios.post(
        url,
        {
          term: '',
          quick_cart: 1,
        },
        { headers: { 'session-token': sessionToken } }
      );
      if (response.data && Array.isArray(response.data)) {
        // Update cart total after fetching items
        getCartTotal();
      }
    } catch (error) {
      console.error('Error fetching cart items:', error);
      toast.error(t('cart.errors.fetchFailed'));
    }
  }, [sessionToken, t, getCartTotal]);

  // Load stored game values
  useEffect(() => {
    const storedGameName = localStorage.getItem('last_game_name');
    const storedGamePassword = localStorage.getItem('last_game_password');
    const storedGameRealm = localStorage.getItem('last_game_realm');

    if (storedGameName) setCurrentGameName(storedGameName);
    if (storedGamePassword) setCurrentGamePassword(storedGamePassword);
    if (storedGameRealm) setCurrentGameRealm(storedGameRealm);
  }, []);

  // Updated to handle quantity
  const handleRemoveCartItem = useCallback(
    (itemId: number, quantity: number = 1) => {
      // Find the item to get its grouping key
      const targetItem = cartItems.find((item) => Number(item.id) === itemId);
      if (!targetItem) return;

      const groupKey = [
        targetItem.stats_description || '',
        targetItem.ocr_string || '',
        targetItem.rarity_id || '',
        targetItem.ladder || '',
        targetItem.hardcore || '',
        targetItem.fiat_price || '',
        targetItem.fg_price || '',
      ].join('|');

      // Get all items in this group
      const groupItems = cartItems.filter((item) => {
        const itemGroupKey = [
          item.stats_description || '',
          item.ocr_string || '',
          item.rarity_id || '',
          item.ladder || '',
          item.hardcore || '',
          item.fiat_price || '',
          item.fg_price || '',
        ].join('|');
        return itemGroupKey === groupKey;
      });

      // Limit quantity to available items
      const itemsToRemove = Math.min(quantity, groupItems.length);

      // Remove specific quantity
      const itemsToRemoveIds = groupItems.slice(0, itemsToRemove).map((item) => item.id);

      // Remove selected items with sequential requests
      const removePromises = itemsToRemoveIds.map((id) => removeCartItem.mutateAsync(id));

      Promise.all(removePromises).then(() => {
        getCartTotal();

        // If the item was marked as unavailable, remove it from that list
        if (unavailableItems.includes(itemId)) {
          setUnavailableItems((prev) => prev.filter((id) => id !== itemId));
        }

        setTimeout(() => {
          notifyCartItemsRemoved();
        }, 300);
      });
    },
    [removeCartItem, getCartTotal, cartItems, unavailableItems]
  );

  // Add a function to find and add more items of the same type
  const handleAddMoreItems = useCallback(
    async (itemId: number) => {
      // Prevent multiple clicks
      if (addingItemIds.includes(itemId)) return;

      setAddingItemIds((prev) => [...prev, itemId]);

      try {
        const loading = toast.loading(t('search.cart.adding', 'Adding item to cart'));

        // Find the target item in the cart
        const targetItem = cartItems.find((item) => Number(item.id) === itemId);
        if (!targetItem) {
          toast.dismiss(loading);
          toast.error(t('search.errors.itemNotFound', 'Item not found'));
          return;
        }

        // Search for available items with the same attributes using public/pss/search endpoint
        const response = await axios.post(
          `${config.backend_base_url}public/pss/search`,
          {
            search_term: targetItem.name || '',
            limit: 100, // Get enough items to find matches
          },
          { headers: { 'session-token': sessionToken } }
        );

        if (!response.data || !Array.isArray(response.data)) {
          toast.dismiss(loading);
          toast.error(t('search.errors.searchFailed', 'Failed to find similar items'));
          return;
        }

        // Create a more comprehensive key from item attributes to find similar items
        const targetKey = [
          targetItem.stats_description || '',
          targetItem.ocr_string || '',
          targetItem.rarity_id || '',
          targetItem.ladder || '',
          targetItem.hardcore || '',
          targetItem.skin_image_path || '', // Using skin_image_path as skin_name
          targetItem.fiat_price || '',
          targetItem.fg_price || '',
        ].join('|');

        // Find available items with matching attributes
        const matchingItems = response.data.filter((item: any) => {
          if (item.status !== 'available') return false;

          const itemKey = [
            item.stats_description || '',
            item.ocr_string || '',
            item.rarity_id || '',
            item.ladder || '',
            item.hardcore || '',
            item.skin_image_path || '', // Using skin_image_path as skin_name
            item.fiat_price || '',
            item.fg_price || '',
          ].join('|');

          return itemKey === targetKey;
        });

        if (matchingItems.length === 0) {
          toast.dismiss(loading);
          toast.error(t('search.errors.noMoreItems', 'No more items available'));
          setUnavailableItems((prev) => [...prev, itemId]);
          return;
        }

        // Add the first matching item to cart
        const itemToAdd = matchingItems[0];

        const addResponse = await axios.post(
          `${config.backend_base_url}d2rs/cart/add`,
          { item_ids: [itemToAdd.id] },
          { headers: { 'session-token': sessionToken } }
        );

        if (addResponse.status === 200) {
          toast.dismiss(loading);
          toast.success(t('search.cart.success', 'Successfully added item to cart'));

          // Invalidate query cache to force refresh
          queryClient.invalidateQueries({ queryKey: ['cartItems'] });

          // Notify components about cart update
          notifyCartItemAdded({ id: itemToAdd.id });

          // Get updated cart data
          getCartItems();
          getCartTotal();
        }
      } catch (error: any) {
        console.error('Error adding to cart:', error);

        // If we get a specific error about no items available, mark this item as unavailable
        if (error.response?.data?.Message?.includes('No more items available')) {
          setUnavailableItems((prev) => [...prev, itemId]);
          toast.error(t('search.errors.noMoreItems', 'No more items available'));
        } else if (error.response?.data?.Message) {
          toast.error(error.response.data.Message);
        } else {
          toast.error(t('search.errors.cartAddFailed', 'Failed to add item to cart'));
        }
      } finally {
        setAddingItemIds((prev) => prev.filter((id) => id !== itemId));
      }
    },
    [addingItemIds, sessionToken, t, queryClient, getCartItems, getCartTotal, cartItems]
  );

  // Update the handleQuantityChange function to use handleAddMoreItems for increments
  const handleQuantityChange = useCallback(
    (itemId: number, delta: number) => {
      const targetItem = cartItems.find((item) => Number(item.id) === itemId);
      if (!targetItem) return;

      const groupKey = [
        targetItem.stats_description || '',
        targetItem.ocr_string || '',
        targetItem.rarity_id || '',
        targetItem.ladder || '',
        targetItem.hardcore || '',
        targetItem.fiat_price || '',
        targetItem.fg_price || '',
      ].join('|');

      // Get all items in this group
      const groupItems = cartItems.filter((item) => {
        const itemGroupKey = [
          item.stats_description || '',
          item.ocr_string || '',
          item.rarity_id || '',
          item.ladder || '',
          item.hardcore || '',
          item.fiat_price || '',
          item.fg_price || '',
        ].join('|');
        return itemGroupKey === groupKey;
      });

      const currentQuantity = groupItems.length;

      if (delta < 0) {
        // Removing items (decreasing quantity)
        const quantityToRemove = Math.min(Math.abs(delta), currentQuantity);
        if (quantityToRemove > 0) {
          handleRemoveCartItem(itemId, quantityToRemove);
        }
      } else if (delta > 0) {
        // Adding more of the same item
        handleAddMoreItems(itemId);
      }
    },
    [cartItems, handleRemoveCartItem, handleAddMoreItems]
  );

  const placeOrder = useCallback(async () => {
    if (!sessionToken || cartItems.length === 0) return;

    if (!currentGameName || !currentGamePassword) {
      toast.error(t('cart.errors.missingGameDetails'));
      return;
    }

    setProcessingOrder(true);

    try {
      // Save values for next time
      localStorage.setItem('last_game_name', currentGameName);
      localStorage.setItem('last_game_password', currentGamePassword);
      localStorage.setItem('last_game_realm', currentGameRealm);

      const itemIds = cartItems.map((item) => item.id);

      const response = await axios.post(
        config.backend_base_url + 'ml/drop/orders/create',
        {
          game_name: currentGameName,
          game_password: currentGamePassword,
          realm: currentGameRealm,
          currency: currency,
          item_ids: itemIds,
          is_halted: createTradeHalted ? 1 : 0,
        },
        { headers: { 'session-token': sessionToken } }
      );

      if (response.status === 200) {
        toast.success(t('cart.orderPlaced'));

        // Notify NewItemPage that an order was placed
        notifyOrderPlaced();

        // Invalidate cart items query to refresh data
        queryClient.invalidateQueries({ queryKey: ['cartItems'] });

        // Update cart total
        getCartTotal();
      }
    } catch (error) {
      console.error('Error creating drop order:', error);
      toast.error(t('cart.errors.orderFailed'));
    } finally {
      setProcessingOrder(false);
    }
  }, [
    sessionToken,
    cartItems,
    currentGameName,
    currentGamePassword,
    currentGameRealm,
    currency,
    createTradeHalted,
    t,
    queryClient,
    getCartTotal,
  ]);

  // useEffect to fetch cart items when component is mounted
  useEffect(() => {
    if (sessionToken) {
      getCartItems();
    }
  }, [sessionToken, getCartItems]);

  // Listen for refresh events
  useEffect(() => {
    const handleRefresh = () => {
      if (sessionToken) {
        // Invalidate query cache to force refresh
        queryClient.invalidateQueries({ queryKey: ['cartItems'] });

        // Fetch cart items whenever refresh event is triggered
        getCartItems();
        getCartTotal();

        // Reset unavailable items list when refreshing
        setUnavailableItems([]);
      }
    };

    // Handle cart item added event
    const handleCartItemAdded = () => {
      if (sessionToken) {
        // Invalidate query cache to force refresh
        queryClient.invalidateQueries({ queryKey: ['cartItems'] });

        // Fetch cart items immediately when a new item is added
        getCartItems();
        getCartTotal();
      }
    };

    window.addEventListener('refreshCartBubble', handleRefresh);
    window.addEventListener('cartItemAdded', handleCartItemAdded);

    return () => {
      window.removeEventListener('refreshCartBubble', handleRefresh);
      window.removeEventListener('cartItemAdded', handleCartItemAdded);
    };
  }, [sessionToken, getCartTotal, getCartItems, queryClient]);

  return (
    <Box sx={styles.container}>
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : cartItems.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '16px' }}>
          <Typography variant="body1" component="p" gutterBottom>
            {t('cart.empty')}
          </Typography>
          <Box mt={2}>
            <Button
              variant="contained"
              color="primary"
              component={Link}
              href="/items?t=search"
              size="small"
            >
              {t('cart.browseItems')}
            </Button>
            <Button
              variant="outlined"
              color="primary"
              component={Link}
              href="/offers"
              size="small"
              sx={{ ml: 1 }}
            >
              {t('cart.viewOffers')}
            </Button>
          </Box>
        </div>
      ) : (
        <>
          <Box sx={styles.scrollBox}>
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Button
                variant="outlined"
                color="secondary"
                size="small"
                fullWidth
                component={Link}
                onClick={() => navigate('/cart')}
                sx={{ mb: 2 }}
              >
                {t('cart.viewFullCart', 'View Full Cart')}
              </Button>
            </Box>
            {groupedItems.map((item) => {
              const groupKey = [
                item.stats_description || '',
                item.ocr_string || '',
                item.rarity_id || '',
                item.ladder || '',
                item.hardcore || '',
                item.fiat_price || '',
                item.fg_price || '',
              ].join('|');
              const quantity = stockMap[groupKey] || 1;
              const isAdding = addingItemIds.includes(item.id);
              const isUnavailable = unavailableItems.includes(item.id);

              return (
                <div key={item.id}>
                  <div
                    style={{
                      width: '100%',
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: '8px',
                      padding: '4px',
                      borderRadius: '8px',
                      border: '1px solid rgba(0, 0, 0, 0.1)',
                      backgroundColor: 'rgba(0, 0, 0, 0.02)',
                      marginBottom: '8px',
                    }}
                  >
                    <ToolsIcon />
                    <div
                      style={{
                        display: 'flex',
                        gap: '4px',
                        flex: 1,
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}
                    >
                      {/* Quantity controls */}
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 0.5,
                        }}
                      >
                        <IconButton
                          size="small"
                          onClick={() => handleQuantityChange(item.id, -1)}
                          disabled={quantity <= 1}
                          sx={{
                            bgcolor: 'background.paper',
                            width: 24,
                            height: 24,
                            padding: 0,
                          }}
                        >
                          <RemoveIcon fontSize="small" />
                        </IconButton>

                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 'bold',
                            bgcolor: 'background.paper',
                            px: 1,
                            py: 0.5,
                            borderRadius: 1,
                            minWidth: '30px',
                            textAlign: 'center',
                          }}
                        >
                          {quantity}
                        </Typography>

                        <IconButton
                          size="small"
                          onClick={() => handleQuantityChange(item.id, 1)}
                          disabled={isAdding || isUnavailable}
                          sx={{
                            bgcolor: 'background.paper',
                            width: 24,
                            height: 24,
                            padding: 0,
                            opacity: isUnavailable ? 0.5 : 1,
                          }}
                        >
                          {isAdding ? <CircularProgress size={16} /> : <AddIcon fontSize="small" />}
                        </IconButton>

                        {/* Bundled price display */}
                        {item.fiat_price && item.fiat_price !== '???' && (
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 'bold',
                              bgcolor: theme.palette.primary.main,
                              color: 'white',
                              px: 1,
                              py: 0.5,
                              borderRadius: 1,
                              ml: 1,
                              display: 'flex',
                              alignItems: 'center',
                            }}
                          >
                            {(parseFloat(item.fiat_price) * quantity).toFixed(2)} €
                          </Typography>
                        )}
                      </Box>

                      <Tooltip title={t('cart.removeItem', 'Remove all')}>
                        <IconButton
                          size="small"
                          sx={{
                            color: (theme) => theme.palette.text.secondary,
                            '&:hover': {
                              color: (theme) => theme.palette.text.primary,
                            },
                          }}
                          aria-label={t('cart.removeItem')}
                          onClick={() => handleRemoveCartItem(item.id, quantity)}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </div>
                  </div>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      alignItems: 'flex-start',
                      backgroundColor: isDarkMode ? '#242424' : '#f5f5f5',
                      borderRadius: '8px',
                      boxSizing: 'border-box',
                    }}
                  >
                    <Box
                      sx={{
                        position: 'relative',
                        borderTopLeftRadius: '8px',
                        borderBottomLeftRadius: '8px',
                        minWidth: 56,
                        minHeight: 200,
                        maxHeight: 200,
                        padding: '16px',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '100%',
                        border: '1px solid',
                        borderColor: secondaryColor,
                      }}
                    >
                      <Tooltip
                        title={
                          <div>
                            {item.ladder === 1 && item.hardcore === 0 && 'Softcore Ladder'}
                            {item.ladder === 0 && item.hardcore === 0 && 'Softcore Non-Ladder'}
                            {item.ladder === 1 && item.hardcore === 1 && 'Hardcore Ladder'}
                            {item.ladder === 0 && item.hardcore === 1 && 'Hardcore Non-Ladder'}
                          </div>
                        }
                        arrow
                      >
                        <span
                          style={{
                            position: 'absolute',
                            top: 8,
                            left: 8,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '2px',
                            zIndex: 1,
                          }}
                        >
                          {item.ladder === 1 && item.hardcore === 0 && (
                            <>
                              <img style={{ width: '24px' }} src={hardcoreOff} alt="hardcoreOff" />
                              <img style={{ width: '24px' }} src={ladderOn} alt="ladderOn" />
                            </>
                          )}
                          {item.ladder === 0 && item.hardcore === 0 && (
                            <>
                              <img style={{ width: '24px' }} src={hardcoreOff} alt="hardcoreOff" />
                              <img style={{ width: '24px' }} src={ladderOff} alt="ladderOff" />
                            </>
                          )}
                          {item.ladder === 1 && item.hardcore === 1 && (
                            <>
                              <img style={{ width: '24px' }} src={hardcoreOn} alt="hardcoreOn" />
                              <img style={{ width: '24px' }} src={ladderOn} alt="ladderOn" />
                            </>
                          )}
                          {item.ladder === 0 && item.hardcore === 1 && (
                            <>
                              <img style={{ width: '24px' }} src={hardcoreOn} alt="hardcoreOn" />
                              <img style={{ width: '24px' }} src={ladderOff} alt="ladderOff" />
                            </>
                          )}
                        </span>
                      </Tooltip>
                      {item.fiat_price && item.fiat_price !== '???' && (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: '8px',
                            right: '8px',
                            backgroundColor: '#142d4c',
                            color: 'white',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontSize: '0.875rem',
                            fontWeight: '700',
                            zIndex: 1,
                            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
                          }}
                        >
                          {item.fiat_price} €
                        </Box>
                      )}
                      <img
                        src={`${config.backend_base_url}skin/${item.skin_image_path}`}
                        alt={item.name}
                        style={{ maxHeight: 150, objectFit: 'contain', borderRadius: 8 }}
                      />
                    </Box>
                    <Box
                      sx={{
                        p: 2,
                        whiteSpace: 'pre-wrap',
                        fontFamily: 'monospace',
                        color: '#000',
                        position: 'relative',
                        border: (theme) => `1px solid ${theme.palette.secondary.main}`,
                        backgroundColor: (theme) =>
                          theme.palette.mode === 'dark'
                            ? theme.palette.grey[900]
                            : theme.palette.grey[100],
                        borderTopRightRadius: '8px',
                        borderBottomRightRadius: '8px',
                        maxWidth: '200%',
                        margin: '0 auto',
                        minHeight: '200px',
                        maxHeight: '200px',
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <Typography
                        variant="body2"
                        component="pre"
                        sx={{
                          margin: 0,
                          fontFamily: 'monospace',
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                          color: '#000',
                          textAlign: 'center',
                          width: '100%',
                          maxHeight: '160px',
                          overflow: 'auto',
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 1,
                            mb: 1,
                          }}
                        >
                          <Tooltip title={getRarityName(item.rarity_id)} placement="top">
                            <Box
                              sx={{
                                width: '12px',
                                height: '12px',
                                minWidth: '12px',
                                minHeight: '12px',
                                borderRadius: '50%',
                                backgroundColor: getColorFromRarity(item.rarity_id),
                                boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                                flexShrink: 0,
                              }}
                            />
                          </Tooltip>
                          <Box
                            sx={{
                              display: 'flex',
                            }}
                          >
                            {item.name}
                          </Box>
                        </Box>
                        {item.stats_description}
                      </Typography>
                      <Tooltip title={t('common.copyToClipboard')} placement="top">
                        <IconButton
                          onClick={() => {
                            navigator.clipboard.writeText(
                              item.name
                                ? `${item.name}\n\n${item.stats_description}`
                                : item.stats_description || ''
                            );
                            toast.success(t('common.copiedToClipboard'));
                          }}
                          size="small"
                          sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            color: (theme) => theme.palette.text.secondary,
                          }}
                        >
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                </div>
              );
            })}
          </Box>

          {/* Metadata form would be conditionally rendered here based on item types */}
          {cartItems.some((item) => item.suffix === 'game') && (
            <Box sx={{ mt: 1, mb: 1, p: 1, bgcolor: 'background.default', borderRadius: 1 }}>
              <Typography variant="subtitle2">{t('cart.gameInfo')}</Typography>
              {/* Render CRUD form for game metadata */}
              {/* This would be implemented based on the specific game metadata requirements */}
            </Box>
          )}

          <Box sx={{ mt: 2, p: 1, bgcolor: 'background.default', borderRadius: 1 }}>
            <Typography variant="subtitle1" align="right" style={{ fontWeight: 600 }}>
              {t('cart.total')}: €{totalFiatPrice} / {totalFgPrice} {t('cart.fg')}
            </Typography>
          </Box>

          {/* Order Form - Moved from Dialog */}
          <Box sx={{ bgcolor: 'background.paper', p: 2, borderRadius: 1 }}>
            <TextField
              label={t('search.game.name')}
              value={currentGameName}
              onChange={(e) => setCurrentGameName(e.target.value)}
              fullWidth
              required
              margin="normal"
              variant="outlined"
              size="small"
              sx={{ marginTop: '10px !important', marginBottom: '10px !important' }}
            />

            <TextField
              label={t('search.game.password')}
              value={currentGamePassword}
              onChange={(e) => setCurrentGamePassword(e.target.value)}
              fullWidth
              required
              margin="normal"
              variant="outlined"
              size="small"
              sx={{ marginTop: '10px !important', marginBottom: '10px !important' }}
            />

            <FormControl fullWidth margin="normal" size="small" sx={styles.selectField}>
              <InputLabel>{t('game.realm')}</InputLabel>
              <Select
                value={currentGameRealm}
                onChange={(e) => setCurrentGameRealm(e.target.value)}
                label={t('game.realm')}
                MenuProps={{
                  PaperProps: {
                    style: {
                      // Ensure dropdown menu doesn't cause layout shifts
                      zIndex: 99999,
                    },
                  },
                }}
              >
                <MenuItem value="europe">{t('search.game.realm.europe')}</MenuItem>
                <MenuItem value="americas">{t('search.game.realm.americas')}</MenuItem>
                <MenuItem value="asia">{t('search.game.realm.asia')}</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth margin="normal" size="small" sx={styles.selectField}>
              <InputLabel>{t('cart.currency')}</InputLabel>
              <Select
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                label={t('cart.currency')}
                MenuProps={{
                  PaperProps: {
                    style: {
                      // Ensure dropdown menu doesn't cause layout shifts
                      zIndex: 99999,
                    },
                  },
                }}
              >
                <MenuItem value="fiat">{t('cart.fiat')}</MenuItem>
                <MenuItem value="fg">{t('cart.fg')}</MenuItem>
              </Select>
            </FormControl>

            <FormControlLabel
              control={
                <Switch
                  checked={createTradeHalted}
                  onChange={(e) => setCreateTradeHalted(e.target.checked)}
                  color="primary"
                />
              }
              label={t('cart.requestLaterDelivery', 'Request delivery later')}
            />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mt: 2 }}>
              <Button
                variant="outlined"
                color="secondary"
                size="small"
                sx={{ flex: 1, mr: 1 }}
                component={Link}
                href="/items?t=search"
              >
                {t('cart.continueShopping')}
              </Button>
              <Button
                variant="contained"
                color="primary"
                size="small"
                disabled={
                  processingOrder ||
                  !currentGameName ||
                  !currentGamePassword ||
                  cartItems.length === 0
                }
                onClick={placeOrder}
                sx={{ flex: 1, ml: 1 }}
              >
                {processingOrder ? <CircularProgress size={24} /> : t('cart.placeOrder')}
              </Button>
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
}
